/**
 * The country dashboard — Figma `01/ Entry/ Dashboard` (node 12:907), reduced to
 * the OWNER-RATIFIED chart-free set.
 *
 * ===========================================================================
 * WHAT IS HERE, AND WHAT IS DELIBERATELY NOT
 * ===========================================================================
 * SHIPPED (all present in the design, or explicitly authorised):
 *   · `Page header` (12:1204) — the greeting, minus its two controls (see
 *     `PageHeader` in `AppShell.tsx` for why the scope toggle and period chips are
 *     omitted rather than rendered dead), extended by Phase 2.5a lever L2 with the
 *     full date and the country context line (see the header block below);
 *   · stage-count cards — an authored extension the owner ratified, because the
 *     brief names them and Figma has no such component (`StageCountCards`);
 *   · `Widget/InquiriesNeedingAction` (12:3101);
 *   · `Widget/RecentActivity` (12:3103), live over SSE;
 *   · "Data as of …" (37:7556) — the design's own freshness line, which matters
 *     more here than it did there: the widgets are polled and streamed, so the
 *     user should be able to see when the page last heard from the server.
 *
 * NOT SHIPPED, and not by omission — by owner decision (PHASE2-REPORT §1.3 E2):
 *   · the 5-card `KPI row` (12:1379) with sparklines and deltas;
 *   · `Hero/BillingChart` (12:2246), the 12-month bar chart with
 *     period-over-period comparison;
 *   · `Widget/DeveloperWorkload` (12:3099) — a Phase 4 surface: it reports on work
 *     orders and CRF assignments, neither of which exists yet. Rendering it against
 *     no data source would mean fabricating rows.
 * Those are Phase 5 (and 4). Building them now is scope invention.
 *
 * ===========================================================================
 * THREE CONCURRENT MOUNTS — THE REASON D-web-1 EXISTS
 * ===========================================================================
 * This page mounts stage-counts, the inquiry list and an SSE ticket mint at the
 * same instant. On a reload with an expired access token, all three 401 together.
 * That is precisely the shape the single-flight refresh mutex in `lib/api.ts` was
 * built for: without it the three parallel refreshes trip Phase 0's
 * revoke-on-rotate theft detection and sign the user out. This page is the test
 * case, so if a fourth concurrent query is ever added here, that is fine — but it
 * must go through `apiRequest`.
 *
 * ===========================================================================
 * PHASE 2.5a · LEVER L2 — THE GREETING IS THE PAGE H1
 * ===========================================================================
 * The greeting already existed (12:1205). What L2 adds is CONTEXT under it: the
 * full date and the country line, so the first thing on the page tells the
 * reader who they are, when it is, and whose records they are looking at.
 *
 * It stays the H1 and gets NO banner, card, gradient or illustration. That is
 * the Squarespace "Welcome, Alex" / Deel "Hey, Alex" pattern the reference pack
 * points at, and it is the reason this lever costs zero layout: the header was
 * already `title` + `subtitle`, and the subtitle simply says more.
 *
 * THE SUBTITLE CARRIES ITS OWN COLOUR, AND THAT IS NOT A STYLE PREFERENCE.
 * `PageHeader` paints its subtitle `text-fg-muted`, which MEASURES 2.63:1 on
 * `bg/page` in light mode — a pre-existing AA failure that predates this phase
 * and is not confined to this screen. L2 triples the amount of text in that
 * slot, so shipping it muted would be knowingly adding sub-AA copy. The line is
 * therefore wrapped in `text-fg-secondary` (4.64:1 light / 6.43:1 dark), which
 * fixes THIS page without reaching into shell furniture that other screens
 * render. The underlying `PageHeader` default is reported as a finding, not
 * silently changed here.
 */

import { useMemo } from 'react';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { COUNTRIES } from '../../domain/countries';
// v19 D3 · W2 (delimited): the period-driven pipeline/work sections — see that file's header.
import { CountryDashboardSections } from '../dashboards/CountryDashboardSections';
import { InquiriesNeedingActionWidget } from './InquiriesNeedingActionWidget';
import { RecentActivityWidget } from './RecentActivityWidget';
import { StageCountCards } from './StageCountCards';
import { fullDate, greetingLine } from './greeting';

export function DashboardPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { me, can } = useSession();
  const canViewInquiries = can(countryCode, 'inquiries.view');
  const profile = COUNTRIES[countryCode];

  // Computed once per mount rather than per render: the greeting must not flicker
  // between "morning" and "afternoon" mid-session on a re-render at 11:59.
  const now = useMemo(() => new Date(), []);
  const asOf = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: profile.tz,
        timeZoneName: 'short',
      }).format(now),
    [now, profile.tz],
  );

  return (
    <div className="flex flex-col gap-6 pb-10">
      <PageHeader
        title={greetingLine(now, me?.name)}
        subtitle={
          <span className="text-fg-secondary">
            {fullDate(now)} · {profile.legalEntity} · {profile.name}
          </span>
        }
      />

      <div className="flex flex-col gap-6 px-6">
        {canViewInquiries ? (
          <StageCountCards countryCode={countryCode} canView={canViewInquiries} />
        ) : null}

        {/*
          ── v19 D3 · W2 — delimited block ──────────────────────────────────────────
          THE PHASE-5 SCOPE THIS HEADER'S DOCBLOCK PARKED, NOW BUILT: pipeline by stage
          with counts AND value, quote-to-order conversion, renewals due, work in flight,
          overdue sign-offs — over `GET /countries/:cc/dashboard?period=`, the first
          thing on this page that TAKES a period, which is why the period filter lives
          inside the section rather than in `PageHeader` (whose own docblock says the
          chips come back "when there is something for them to filter"). Sections arrive
          per seat and absent ones are absent; a seat with none of the three keys gets
          nothing here and the page above is unchanged. It goes through `apiRequest`, so
          it is the fourth concurrent mount the D-web-1 mutex was built for.
        */}
        <CountryDashboardSections countryCode={countryCode} />
        {/* ── end v19 D3 · W2 block ─────────────────────────────────────────────── */}

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
          <InquiriesNeedingActionWidget countryCode={countryCode} canView={canViewInquiries} />
          <RecentActivityWidget countryCode={countryCode} />
        </div>

        {/* Figma 37:7556. The country's own timezone, because "today 9:41 AM" means
            something different to a coordinator in Kinshasa than to one in Dubai. */}
        <p className="text-label text-fg-muted">Data as of today {asOf}</p>
      </div>
    </div>
  );
}
